import { findImportBindingForLocalName, type DbImportRow } from '../../symbol-index/sqlite/symbol-index-import-sqlite.js';
import { resolveImportSourcePath } from '../../resolve-module-import-path.js';
import type { DbTagRow } from '../types.js';

const MAX_REEXPORT_DEPTH = 8;
const REEXPORT_IMPORT_KINDS = new Set(['reexport', 'type', 'star']);

function resolveBindingSourcePath(binding: DbImportRow, workspaceFiles: ReadonlySet<string>): string | null {
  if (binding.source_file_path && workspaceFiles.has(binding.source_file_path)) {
    return binding.source_file_path;
  }
  const resolved = resolveImportSourcePath(binding.file_path, binding.module_specifier, workspaceFiles);
  if (resolved) return resolved;
  return binding.source_file_path;
}

type DefsLookup = (filePath: string, name: string) => readonly DbTagRow[];

function chainVisitKey(filePath: string, name: string): string {
  return `${filePath}:${name}`;
}

function findReexportBindingForName(
  imports: readonly DbImportRow[],
  localName: string,
  ref?: Pick<DbTagRow, 'kind'>,
): DbImportRow | null {
  const reexports = imports.filter(
    (row) => row.local_name === localName && REEXPORT_IMPORT_KINDS.has(row.import_kind),
  );
  if (reexports.length === 0) return null;
  if (reexports.length === 1) return reexports[0]!;

  if (ref?.kind === 'type') {
    const typeRows = reexports.filter((row) => row.import_kind === 'type');
    if (typeRows.length > 0) return typeRows[typeRows.length - 1]!;
  } else if (ref?.kind != null) {
    const valueRows = reexports.filter((row) => row.import_kind !== 'type');
    if (valueRows.length > 0) return valueRows[valueRows.length - 1]!;
  }

  return reexports[reexports.length - 1]!;
}

function resolveThroughStarExports(
  filePath: string,
  name: string,
  lookupDefsInFileNamed: DefsLookup,
  lookupImportsForFile: (filePath: string) => readonly DbImportRow[],
  workspaceFiles: ReadonlySet<string>,
  ref: Pick<DbTagRow, 'kind'> | undefined,
  visited: Set<string>,
  depth: number,
): DbTagRow[] {
  if (depth > MAX_REEXPORT_DEPTH) return [];

  const stars = lookupImportsForFile(filePath).filter((row) => row.import_kind === 'star');
  for (const star of stars) {
    const sourcePath = resolveBindingSourcePath(star, workspaceFiles);
    if (!sourcePath) continue;
    const visitKey = chainVisitKey(sourcePath, name);
    if (visited.has(visitKey)) continue;
    visited.add(visitKey);

    const direct = lookupDefsInFileNamed(sourcePath, name);
    if (direct.length > 0) return [...direct];

    const upstream = findReexportBindingForName(lookupImportsForFile(sourcePath), name, ref);
    if (upstream) {
      const resolved = followImportBinding(
        upstream,
        lookupDefsInFileNamed,
        lookupImportsForFile,
        workspaceFiles,
        ref,
        visited,
        depth + 1,
      );
      if (resolved.length > 0) return resolved;
    }

    const nested = resolveThroughStarExports(
      sourcePath,
      name,
      lookupDefsInFileNamed,
      lookupImportsForFile,
      workspaceFiles,
      ref,
      visited,
      depth + 1,
    );
    if (nested.length > 0) return nested;
  }

  return [];
}

/** Follow import and re-export bindings to definition rows in the symbol index. */
export function followImportBinding(
  binding: DbImportRow,
  lookupDefsInFileNamed: DefsLookup,
  lookupImportsForFile: (filePath: string) => readonly DbImportRow[],
  workspaceFiles: ReadonlySet<string>,
  ref?: Pick<DbTagRow, 'kind'>,
  visited = new Set<string>(),
  depth = 0,
): DbTagRow[] {
  if (depth > MAX_REEXPORT_DEPTH) return [];

  const sourcePath = resolveBindingSourcePath(binding, workspaceFiles);
  if (!sourcePath) return [];

  const visitKey = chainVisitKey(sourcePath, binding.source_name);
  if (visited.has(visitKey)) return [];
  visited.add(visitKey);

  const upstreamBinding = findReexportBindingForName(
    lookupImportsForFile(sourcePath),
    binding.source_name,
    ref,
  );
  if (upstreamBinding) {
    return followImportBinding(
      upstreamBinding,
      lookupDefsInFileNamed,
      lookupImportsForFile,
      workspaceFiles,
      ref,
      visited,
      depth + 1,
    );
  }

  const fromStars = resolveThroughStarExports(
    sourcePath,
    binding.source_name,
    lookupDefsInFileNamed,
    lookupImportsForFile,
    workspaceFiles,
    ref,
    visited,
    depth + 1,
  );
  if (fromStars.length > 0) return fromStars;

  const direct = lookupDefsInFileNamed(sourcePath, binding.source_name);
  if (direct.length > 0) return [...direct];

  const fallbackBinding = findImportBindingForLocalName(
    lookupImportsForFile(sourcePath),
    binding.source_name,
    ref,
  );
  if (fallbackBinding && fallbackBinding !== upstreamBinding) {
    return followImportBinding(
      fallbackBinding,
      lookupDefsInFileNamed,
      lookupImportsForFile,
      workspaceFiles,
      ref,
      visited,
      depth + 1,
    );
  }

  return [];
}

export function hasImportBindingForName(imports: readonly DbImportRow[], localName: string): boolean {
  return imports.some((row) => row.local_name === localName);
}

/** @deprecated Use followImportBinding */
export const resolveImportedDefinitions = followImportBinding;
